import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { ASSETS } from "../lib/constants";

const ease = [0.76, 0, 0.24, 1];

const MaskedLine = ({ children, delay = 0 }) => (
  <span className="mask-line">
    <motion.span
      className="block"
      initial={{ y: "110%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 1.15, delay, ease }}
    >
      {children}
    </motion.span>
  </span>
);

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const yImg = useTransform(scrollYProgress, [0, 1], [0, 180]);
  const yTitle = useTransform(scrollYProgress, [0, 1], [0, -60]);
  const opacity = useTransform(scrollYProgress, [0, 0.75], [1, 0.3]);

  return (
    <section
      id="top"
      ref={ref}
      data-testid="hero-section"
      className="relative min-h-[100svh] pt-28 md:pt-32 pb-16 overflow-hidden vignette"
    >
      {/* Ambient saffron glow */}
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(ellipse 55% 40% at 78% 32%, rgba(217,105,28,0.10), transparent 60%)",
        }}
      />
      {/* Faint chakra */}
      <div className="absolute inset-0 pointer-events-none flex items-center justify-start pl-[15%]">
        <svg
          viewBox="0 0 200 200"
          className="w-[70vw] h-[70vw] max-w-[820px] max-h-[820px] opacity-[0.06]"
          fill="none"
        >
          <circle cx="100" cy="100" r="90" stroke="#1A1611" strokeWidth="0.5" />
          <circle cx="100" cy="100" r="70" stroke="#1A1611" strokeWidth="0.3" />
          {Array.from({ length: 24 }).map((_, i) => (
            <line
              key={i}
              x1="100"
              y1="100"
              x2="100"
              y2="10"
              stroke="#1A1611"
              strokeWidth="0.4"
              transform={`rotate(${i * 15} 100 100)`}
            />
          ))}
        </svg>
      </div>

      <div className="mx-auto max-w-[1600px] px-6 md:px-12 lg:px-16 relative">
        <div className="grid grid-cols-12 gap-6 md:gap-10 items-start mt-8 md:mt-10">
          {/* LEFT — Typography */}
          <motion.div style={{ y: yTitle, opacity }} className="col-span-12 lg:col-span-8 relative">
            <h1
              className="font-display font-light leading-[0.86] tracking-[-0.035em] text-ink"
              style={{ fontVariationSettings: '"SOFT" 80' }}
            >
              <MaskedLine delay={0.2}>
                <span className="block text-[17vw] md:text-[13.5vw] lg:text-[11vw] xl:text-[10.2vw]">
                  Nav<span className="italic font-light">Sankalp</span> <span className="text-saffron">IAS</span>
                </span>
              </MaskedLine>
            </h1>

            <div className="mt-6 md:mt-8 max-w-2xl">
              <MaskedLine delay={0.4}>
                <p
                  className="font-display italic text-2xl md:text-3xl lg:text-4xl text-ink/85 leading-[1.15] font-light"
                  style={{ fontVariationSettings: '"SOFT" 100' }}
                >
                  Where your Resolution <br className="hidden md:block" />
                  meets <span className="text-saffron">Proven Methods.</span>
                </p>
              </MaskedLine>
            </div>

            <div className="mt-10 md:mt-14">
              <MaskedLine delay={0.55}>
                <p className="text-base md:text-lg text-ink/70 leading-relaxed font-light max-w-xl">
                  A mentorship-first institute preparing India's next generation of civil servants — with strategy, structure and honesty that actually work.
                </p>
              </MaskedLine>
            </div>
          </motion.div>

          {/* RIGHT — Portrait */}
          <motion.div style={{ y: yImg }} className="col-span-12 lg:col-span-4 relative">
            <motion.div
              initial={{ scale: 1.08, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 1.4, delay: 0.5, ease }}
              className="relative"
            >
              <div className="frame-mount relative">
                <div className="relative overflow-hidden">
                  <img
                    src={ASSETS.portrait}
                    alt="Himanshu Bhansali Sir — Founder, NavSankalpIAS"
                    className="w-full h-auto object-cover"
                    style={{ aspectRatio: "3/4" }}
                    data-testid="hero-portrait"
                  />
                  <div
                    className="absolute inset-0 pointer-events-none border"
                    style={{ borderColor: "rgba(217,105,28,0.45)" }}
                  />
                </div>
                <div className="flex items-end justify-between mt-3 px-1">
                  <span className="text-[11px] tracking-[0.22em] uppercase text-ink/55 font-medium">Founder & Mentor</span>
                </div>
              </div>

            </motion.div>
          </motion.div>
        </div>

        {/* Bottom stats — institute values, no program-specific info */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.6, duration: 0.8 }}
          className="mt-16 md:mt-24 grid grid-cols-2 md:grid-cols-4 gap-y-6 border-t border-ink-15 pt-6"
        >
          {[
            { k: "Founder", v: "Himanshu Bhansali" },
            { k: "Approach", v: "Mentorship-First" },
            { k: "Attention", v: "One-to-One" },
            { k: "Delivery", v: "Online · Evening" },
          ].map((i) => (
            <div key={i.k}>
              <div className="text-[10px] tracking-[0.3em] uppercase text-ink/45 font-medium">{i.k}</div>
              <div
                className="font-display text-2xl md:text-3xl text-ink leading-tight mt-1"
                style={{ fontVariationSettings: '"SOFT" 40' }}
              >
                {i.v}
              </div>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}
